import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import { useEffect, useState } from "react";
import ResponsiveTable, { Column } from "@/components/Usados/ResponsiveTable";
import ActionMenu from "@/components/Usados/ActionMenu";
import { Modal } from "@/components/Usados/modal";
import Button from "@/components/ui/button/Button";
import { useNavigate } from "react-router";
import { Eye, Pencil, Plus, Trash } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Rol } from "@/types/roles";
import { apiFetch } from "@/service/api";

interface ConfiguracionEmpresa {
  id: number;
  moneda: string;
  idioma: string;
  zona_horaria: string;
  logo: string;
  fecha_creacion: string;
}

export default function ListarConfiguraciones() {
  const { user } = useAuth();
  const rolId: number = user?.rol;
  const canDelete = rolId !== Rol.COLABORADOR && rolId !== Rol.ASISTENTE;
  const [configuraciones, setConfiguraciones] = useState<
    ConfiguracionEmpresa[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<number | null>(null);
  const navigate = useNavigate();

  const fetchConfiguraciones = async () => {
    try {
      const response = await apiFetch(`configuracion-empresa/find-all`);
      if (response.ok) {
        const data = await response.json();
        setConfiguraciones(Array.isArray(data) ? data : [data]);
      }
    } catch (error) {
      console.error("Error fetching configurations:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfiguraciones();
  }, []);

  const handleDelete = (id: number) => {
    setConfigToDelete(id);
    setIsModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!configToDelete) return;
    try {
      const response = await apiFetch(
        `configuracion-empresa/remove/${configToDelete}`,
        {
          method: "DELETE",
        },
      );
      if (response.ok) {
        setConfiguraciones(
          configuraciones.filter((c) => c.id !== configToDelete),
        );
        setIsModalOpen(false);
        setConfigToDelete(null);
      }
    } catch (error) {
      console.error("Error deleting configuration:", error);
    }
  };

  const columns: Column<ConfiguracionEmpresa>[] = [
    { label: "Moneda", key: "moneda" },
    { label: "Idioma", key: "idioma" },
    { label: "Zona Horaria", key: "zona_horaria" },
    {
      label: "Acciones",
      key: "actions",
      render: (_, row) => (
        <ActionMenu
          actions={[
            {
              label: "Ver",
              icon: <Eye className="size-4" />,
              onClick: () =>
                navigate(`/multiempresa/configuracion_empresas/ver/${row.id}`),
            },
            {
              label: "Editar",
              icon: <Pencil className="size-4" />,
              onClick: () =>
                navigate(
                  `/multiempresa/configuracion_empresas/update/${row.id}`,
                ),
            },
            ...(canDelete
              ? [
                  {
                    label: "Eliminar",
                    icon: <Trash className="size-4" />,
                    onClick: () => handleDelete(row.id),
                    className: "text-red-500 hover:bg-red-50",
                  },
                ]
              : []),
          ]}
        />
      ),
    },
  ];

  return (
    <div>
      <PageBreadcrumb pageTitle="Configuraciones de Empresa" />
      <div className="rounded-2xl border border-gray-200 bg-white px-5 py-7 dark:border-gray-800 dark:bg-white/[0.03] xl:px-10 xl:py-12">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between mb-7">
          <h3 className="font-semibold text-gray-800 text-theme-xl dark:text-white/90">
            Configuraciones Registradas
          </h3>
          <Button
            variant="primary"
            size="sm"
            startIcon={<Plus className="size-4" />}
            onClick={() =>
              navigate("/multiempresa/configuracion_empresas/create")
            }
          >
            Nueva Configuracion
          </Button>
        </div>
        {loading ? (
          <div className="flex justify-center py-10">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-500"></div>
          </div>
        ) : (
          <ResponsiveTable columns={columns} data={configuraciones} />
        )}
      </div>
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        className="max-w- p-6"
      >
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100 text-red-600 dark:bg-red-500/10">
            <Trash className="size-8" />
          </div>
          <h3 className="mb-2 text-xl font-bold text-gray-800 dark:text-white/90">
            Confirmar Eliminacion
          </h3>
          <p className="mb-7 text-sm text-gray-500 dark:text-gray-400">
            ¿Estas seguro de que deseas eliminar esta configuracion? Esta accion
            no se puede deshacer.
          </p>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setIsModalOpen(false)}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={confirmDelete}
              className="flex-1 bg-red-600 hover:bg-red-700!border-red-600"
            >
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
